(() => {
  'use strict';

  if (window.ZinVoltCorePageV2Loaded) return;
  window.ZinVoltCorePageV2Loaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const coreLocale = (root.lang || 'nl').toLowerCase().split('-')[0];
  const coreCopyByLocale = {
    nl: {
      modes: { self: 'Zelfgebruik', dynamic: 'Dynamische energie', insight: 'Inzicht en statistieken' },
      kickers: { self: 'Slimme modus', dynamic: 'Slimme modus', insight: 'Altijd inzicht' }
    },
    en: {
      modes: { self: 'Self-consumption', dynamic: 'Dynamic energy', insight: 'Insights and statistics' },
      kickers: { self: 'Smart mode', dynamic: 'Smart mode', insight: 'Always informed' }
    },
    fr: {
      modes: { self: 'Autoconsommation', dynamic: 'Énergie dynamique', insight: 'Informations et statistiques' },
      kickers: { self: 'Mode intelligent', dynamic: 'Mode intelligent', insight: 'Toujours informé' }
    }
  };
  const coreCopy = coreCopyByLocale[coreLocale] || coreCopyByLocale.nl;
  let headerHeight = 72;
  let subnavHeight = 54;

  const getHeader = () => document.querySelector(
    '.shopify-section-header .header, .section-header .header, .shopify-section-group-header-group .header, header.header'
  ) || document.querySelector(
    '.shopify-section-group-header-group, .section-header, .shopify-section.section-header, header.header-wrapper, sticky-header'
  );

  const updateOffsets = () => {
    const header = getHeader();
    headerHeight = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 72;
    const subnav = document.querySelector('[data-zvc-subnav]');
    const subnavIsVisible = subnav && !window.matchMedia('(max-width: 989px)').matches;
    subnavHeight = subnavIsVisible
      ? Math.max(0, Math.round(subnav.getBoundingClientRect().height || 40))
      : 0;
    root.style.setProperty('--zvc-header-height', `${headerHeight}px`);
    root.style.setProperty('--zvc-subnav-height', `${subnavHeight}px`);
  };

  const getScrollOffset = () => {
    const subnav = document.querySelector('[data-zvc-subnav]');
    const navOffset = subnav && subnav.classList.contains('is-sticky') ? subnavHeight : 0;
    return headerHeight + navOffset + 14;
  };

  const smoothScrollTo = (target) => {
    if (!target) return;
    updateOffsets();
    const top = target.getBoundingClientRect().top + window.scrollY - getScrollOffset();
    window.scrollTo({
      top: Math.max(0, top),
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
  };

  const initSmoothLinks = (scope = document) => {
    scope.querySelectorAll('a[href^="#"]:not([href="#"])').forEach((link) => {
      if (link.dataset.zvcScrollReady === 'true') return;
      const href = link.getAttribute('href');
      if (!href || href === '#zv-advies-popup') return;
      const target = document.querySelector(href);
      if (!target) return;

      link.dataset.zvcScrollReady = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        smoothScrollTo(target);
      });
    });
  };

  const initReveals = (scope = document) => {
    const items = Array.from(scope.querySelectorAll('[data-zvc-reveal], [data-zv-reveal]')).filter(
      (item) => item.dataset.zvcRevealReady !== 'true'
    );
    if (!items.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.zvcRevealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries, activeObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        activeObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

    items.forEach((item) => {
      item.dataset.zvcRevealReady = 'true';
      observer.observe(item);
    });
  };

  const initSubnav = () => {
    const wrap = document.querySelector('[data-zvc-subnav-wrap]');
    const nav = document.querySelector('[data-zvc-subnav]');
    if (!wrap || !nav || nav.dataset.zvcReady === 'true') return;
    nav.dataset.zvcReady = 'true';

    const links = Array.from(nav.querySelectorAll('[data-zvc-nav-link]'));
    const targets = links
      .map((link) => {
        const target = document.querySelector(link.getAttribute('href'));
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    let triggerY = 0;
    let frame = 0;
    let activeLink = null;

    const alignToHeader = () => {
      const header = getHeader();
      if (!header) return;

      const rect = header.getBoundingClientRect();
      const innerInset = Math.max(24, Math.min(56, Math.round(rect.width * 0.035)));
      const right = Math.max(10, Math.round(window.innerWidth - rect.right + innerInset));
      const width = Math.max(560, Math.min(840, Math.round(rect.width * 0.48)));
      const top = Math.max(0, Math.round(rect.bottom));

      nav.style.setProperty('--zvc-subnav-right', `${right}px`);
      nav.style.setProperty('--zvc-subnav-width', `${width}px`);
      nav.style.setProperty('--zvc-subnav-top', `${top}px`);
    };

    const measure = () => {
      updateOffsets();
      alignToHeader();
      const hero = document.querySelector('#overview') || document.querySelector('[data-zv-product-hero]');
      if (hero) {
        const heroRect = hero.getBoundingClientRect();
        triggerY = heroRect.top + window.scrollY + heroRect.height;
      } else {
        triggerY = wrap.getBoundingClientRect().top + window.scrollY;
      }
    };

    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        alignToHeader();
        const shouldStick = window.scrollY + headerHeight + 8 >= triggerY;
        nav.classList.toggle('is-sticky', shouldStick);

        if (!targets.length) return;
        const probe = window.scrollY + getScrollOffset() + 110;
        let active = targets[0];
        targets.forEach((item) => {
          const top = item.target.getBoundingClientRect().top + window.scrollY;
          if (top <= probe) active = item;
        });

        targets.forEach((item) => {
          const selected = item === active;
          item.link.classList.toggle('is-active', selected);
          if (selected) item.link.setAttribute('aria-current', 'location');
          else item.link.removeAttribute('aria-current');
        });

        if (active.link !== activeLink) {
          activeLink = active.link;
          if (window.innerWidth <= 989 && nav.classList.contains('is-sticky')) {
            activeLink.scrollIntoView({
              behavior: reducedMotion.matches ? 'auto' : 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      });
    };

    measure();
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      update();
    });

    if ('ResizeObserver' in window) {
      const header = getHeader();
      if (header) new ResizeObserver(() => {
        measure();
        update();
      }).observe(header);
    }
  };

  const initDayCycle = (scope = document) => {
    scope.querySelectorAll('[data-zvc-day-cycle]').forEach((section) => {
      if (section.dataset.zvcDayReady === 'true') return;
      section.dataset.zvcDayReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-zvc-day-tab]'));
      const stage = section.querySelector('[data-zvc-day-stage]');
      const caption = section.querySelector('[data-zvc-day-caption]');
      if (!tabs.length || !stage || !caption) return;

      const captions = {
        morning: 'In de ochtend gebruikt je woning energie terwijl de zonneopwek nog beperkt is.',
        afternoon: 'Rond de middag is er vaak meer zonneopwek dan je woning direct nodig heeft. De Core slaat het overschot op.',
        evening: 'Wanneer de zonneopwek afneemt gebruikt je woning de eerder opgeslagen energie.'
      };

      const activate = (tab, focus = false) => {
        const mode = tab.dataset.zvcDayTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        stage.dataset.zvcDayStage = mode;
        caption.textContent = captions[mode] || '';
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next], true);
        });
      });
    });
  };

  const initApp = (scope = document) => {
    scope.querySelectorAll('[data-zvc-app]').forEach((section) => {
      if (section.dataset.zvcAppReady === 'true') return;
      section.dataset.zvcAppReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-zvc-app-tab]'));
      const phone = section.querySelector('[data-zvc-app-phone]');
      const modeLabel = section.querySelector('[data-zvc-app-mode]');
      const modeKicker = section.querySelector('[data-zvc-app-kicker]');
      if (!tabs.length || !phone) return;

      const labels = coreCopy.modes;
      const kickers = coreCopy.kickers;

      const activate = (tab, focus = false) => {
        const mode = tab.dataset.zvcAppTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        phone.dataset.zvcAppPhone = mode;
        if (modeLabel) modeLabel.textContent = labels[mode] || '';
        if (modeKicker) modeKicker.textContent = kickers[mode] || '';
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowDown') next = (index + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next], true);
        });
      });
    });
  };

  const initDaysLeft = (scope = document) => {
    scope.querySelectorAll('[data-zvc-days-left]').forEach((node) => {
      if (node.dataset.zvcDaysReady === 'true') return;
      node.dataset.zvcDaysReady = 'true';
      const target = new Date('2027-01-01T00:00:00+01:00').getTime();
      const update = () => {
        const diff = target - Date.now();
        const days = Math.max(0, Math.ceil(diff / 86400000));
        node.textContent = new Intl.NumberFormat(root.lang || 'nl-NL').format(days);
      };
      update();
    });
  };

  const initSpecificationAccordions = (scope = document) => {
    scope.querySelectorAll('[data-zvc-spec-tabs]').forEach((section) => {
      if (section.dataset.zvcSpecReady === 'true') return;
      section.dataset.zvcSpecReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-zvc-spec-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-zvc-spec-panel]'));
      if (!tabs.length || !panels.length) return;

      const activate = (tab, focus = false) => {
        const key = tab.dataset.zvcSpecTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });

        panels.forEach((panel) => {
          const active = panel.dataset.zvcSpecPanel === key;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });

        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next], true);
        });
      });
    });
  };

  const initHomepageFaq = (scope = document) => {
    scope.querySelectorAll('[data-zv-faq]').forEach((faq) => {
      if (faq.dataset.zvcFaqReady === 'true') return;
      faq.dataset.zvcFaqReady = 'true';
      const items = Array.from(faq.querySelectorAll('details'));

      const openItem = (item, content) => {
        if (!content) return;
        if (reducedMotion.matches) {
          item.open = true;
          content.style.height = 'auto';
          return;
        }
        item.open = true;
        content.style.height = '0px';
        content.getBoundingClientRect();
        content.style.height = `${content.scrollHeight}px`;
        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          content.style.height = 'auto';
          content.removeEventListener('transitionend', onEnd);
        };
        content.addEventListener('transitionend', onEnd);
      };

      const closeItem = (item, content) => {
        if (!content) return;
        if (reducedMotion.matches) {
          item.open = false;
          content.style.height = '0px';
          return;
        }
        content.style.height = `${content.scrollHeight}px`;
        content.getBoundingClientRect();
        content.style.height = '0px';
        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          item.open = false;
          content.removeEventListener('transitionend', onEnd);
        };
        content.addEventListener('transitionend', onEnd);
      };

      items.forEach((item) => {
        const summary = item.querySelector('summary');
        const content = item.querySelector('[data-zv-faq-content]');
        if (!summary || !content) return;
        content.style.height = item.open ? 'auto' : '0px';
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.open) {
            closeItem(item, content);
            return;
          }
          items.forEach((other) => {
            if (other === item || !other.open) return;
            closeItem(other, other.querySelector('[data-zv-faq-content]'));
          });
          openItem(item, content);
        });
      });
    });
  };

  const initHomepageReviews = (scope = document) => {
    scope.querySelectorAll('[data-zv-reviews-slider]').forEach((slider) => {
      if (slider.dataset.zvcReviewsReady === 'true') return;
      slider.dataset.zvcReviewsReady = 'true';

      const track = slider.querySelector('[data-zv-reviews-track]');
      const cards = Array.from(track?.children || []);
      const dots = Array.from(slider.querySelectorAll('[data-zv-review-dot]'));
      if (!track || !cards.length || !dots.length) return;

      const setDot = (index) => {
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          cards[index]?.scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            inline: 'start',
            block: 'nearest'
          });
          setDot(index);
        });
      });

      let frame = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const trackLeft = track.getBoundingClientRect().left;
          let closest = 0;
          let distance = Infinity;
          cards.forEach((card, index) => {
            const current = Math.abs(card.getBoundingClientRect().left - trackLeft);
            if (current < distance) {
              distance = current;
              closest = index;
            }
          });
          setDot(closest);
        });
      }, { passive: true });
    });
  };

  const init = (scope = document) => {
    if (!document.querySelector('[data-zvc-subnav]')) return;
    document.body.classList.add('zvc-core-active');
    updateOffsets();
    initSmoothLinks(scope);
    initReveals(scope);
    initSubnav();
    initDayCycle(scope);
    initApp(scope);
    initDaysLeft(scope);
    initSpecificationAccordions(scope);
    initHomepageFaq(scope);
    initHomepageReviews(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    updateOffsets();
    init(event.target);
  });
})();
